import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API } from "../App";
import { countryToFlag } from "../lib/countryFlag";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, TrendingUp, BarChart2, FileText, Radio } from "lucide-react";
import { toast } from "sonner";
import MatchAnalysisModal from "../components/MatchAnalysisModal";
import LiveStreamModal from "../components/LiveStreamModal";
import TopNav from "../components/TopNav";
import TrustStars from "../components/TrustStars";
import LiveInPlayPanel from "../components/LiveInPlayPanel";
import FollowTeamButton from "../components/FollowTeamButton";
import WhyPanel from "../components/WhyPanel";
import EngineComparisonPanel from "../components/EngineComparisonPanel";
import HitTrackerBadge from "../components/HitTrackerBadge";
import { HelpCircle, Flame } from "lucide-react";

const confTone = (c) =>
  c >= 75 ? "text-success" : c >= 60 ? "text-warning" : "text-destructive";

const riskTone = (risk) =>
  risk === "Low" ? "bg-success/10 text-success border-success/30"
  : risk === "High" ? "bg-destructive/10 text-destructive border-destructive/30"
  : "bg-warning/10 text-warning border-warning/30";

const MatchDetailsPage = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [showStream, setShowStream] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [livePayload, setLivePayload] = useState(null);
  const [whyPanelMarket, setWhyPanelMarket] = useState(null);

  useEffect(() => { fetchMatchDetails(); }, [matchId]);

  // Poll live in-play data — the backend resolves is_live from the
  // live snapshot (which is more current than db.matches.status), so
  // we always poll for any match whose kickoff is within ±5h of now.
  useEffect(() => {
    if (!data?.match) return;
    const fid = data.match.fixture_id;
    if (!fid) return;
    const koTs = data.match.timestamp ? data.match.timestamp * 1000 : null;
    const now = Date.now();
    const inLiveWindow = !koTs || (now - koTs > -60 * 60 * 1000 && now - koTs < 5 * 60 * 60 * 1000);
    if (!inLiveWindow) return;

    let cancelled = false;
    const fetchLive = async () => {
      try {
        const { data: lv } = await axios.get(`${API}/live/match/${fid}`);
        if (!cancelled) setLivePayload(lv);
      } catch (_) {
        // silent
      }
    };
    fetchLive();
    const id = setInterval(fetchLive, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [data]);

  const fetchMatchDetails = async () => {
    try { setData((await axios.get(`${API}/predictions/${matchId}`)).data); setLoading(false); }
    catch { toast.error("Failed to load match details"); setLoading(false); }
  };

  const fetchDetailedAnalysis = async () => {
    setLoadingAnalysis(true);
    try {
      const lang = (i18n?.language || 'en').split('-')[0];
      setAnalysisData((await axios.get(`${API}/predictions/${matchId}/analysis`, { params: { lang } })).data);
      setShowAnalysisModal(true);
    } catch { toast.error("Failed to load detailed analysis"); }
    finally { setLoadingAnalysis(false); }
  };

  // Iter 137 — when the user lands here from a TVBadge click (?tv=1),
  // auto-fetch the analysis and jump straight to the "Where to Watch"
  // section so they don't have to hunt for it.
  useEffect(() => {
    if (searchParams.get("tv") !== "1") return;
    if (!data || loadingAnalysis || showAnalysisModal) return;
    fetchDetailedAnalysis();
  // eslint-disable-next-line
  }, [data, searchParams]);

  // Once the modal is open after a ?tv=1 deep-link, scroll the
  // "Where to Watch" card into view.
  useEffect(() => {
    if (!showAnalysisModal || searchParams.get("tv") !== "1") return;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="where-to-watch-section"]');
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      // Strip the param so a manual close doesn't reopen on next render.
      const sp = new URLSearchParams(searchParams);
      sp.delete("tv");
      setSearchParams(sp, { replace: true });
    }, 350);
    return () => clearTimeout(t);
  // eslint-disable-next-line
  }, [showAnalysisModal]);

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "long", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit",
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="font-display text-xl">Match not found</p>
          <Button onClick={() => navigate("/")} className="mt-4 rounded-full" data-testid="back-home-btn">
            Back to home
          </Button>
        </div>
      </div>
    );
  }

  const { match, predictions } = data;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-muted-foreground hover:text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {/* Match header */}
        <section className="nd-card p-8 mb-8 nd-fade-up">
          <div className="text-center mb-6">
            <Badge variant="outline" className="border-border text-muted-foreground bg-transparent">
              <span className="mr-1" aria-hidden>{countryToFlag(match.league?.country)}</span>
              {match.league.name} · {match.league.country}
            </Badge>
            <p className="text-muted-foreground text-sm mt-3">{formatDate(match.date)}</p>
          </div>

          <div className="flex items-center justify-center gap-8 sm:gap-14 mb-6 flex-wrap">
            <div className="text-center min-w-[180px]">
              <img src={match.home_team.logo} alt={match.home_team.name}
                   className="w-24 h-24 object-contain mx-auto mb-3" />
              <h2 className="font-display text-2xl font-bold tracking-tight inline-flex items-center gap-1.5">
                {match.home_team.name}
                <FollowTeamButton teamId={match.home_team.id} teamName={match.home_team.name} size="lg" />
              </h2>
            </div>
            {/* Iter 78 — Show the actual score for finished / live matches.
                Falls back to "VS" for pre-match fixtures.  Status codes come
                straight from API-Sports: FT/AET/PEN/AWD = finished;
                1H/2H/HT/ET = live; NS/PST/CANC = not started/postponed. */}
            {(() => {
              const st = String(match.status || "").toUpperCase();
              const finished = ["FT", "AET", "PEN", "AWD"].includes(st);
              const live = ["1H", "2H", "HT", "ET"].includes(st);
              const hasGoals = typeof match.goals_home === "number" && typeof match.goals_away === "number";
              if ((finished || live) && hasGoals) {
                const statusLabel = finished
                  ? (st === "AET" ? "AET" : st === "PEN" ? "PEN" : st === "AWD" ? "AWD" : "Full-time")
                  : (st === "HT" ? "Half-time" : "Live");
                return (
                  <div className="text-center" data-testid="md-final-score">
                    <div className="font-display text-4xl sm:text-5xl font-black tracking-tight tabular-nums leading-none">
                      {match.goals_home}
                      <span className="mx-2 text-muted-foreground">–</span>
                      {match.goals_away}
                    </div>
                    {typeof match.ht_home === "number" && typeof match.ht_away === "number" && (
                      <div className="mt-1 text-[11px] text-muted-foreground tracking-wide">
                        HT&nbsp;{match.ht_home}–{match.ht_away}
                      </div>
                    )}
                    <div
                      className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] ${
                        live
                          ? "bg-amber-500/15 text-amber-500 border border-amber-500/40"
                          : "bg-emerald-500/15 text-emerald-400 border border-emerald-500/40"
                      }`}
                      data-testid="md-score-status"
                    >
                      {live && (
                        <span className="relative flex w-1.5 h-1.5">
                          <span className="absolute inline-flex w-full h-full rounded-full bg-amber-400 opacity-75 animate-ping" />
                          <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-amber-400" />
                        </span>
                      )}
                      {statusLabel}
                    </div>
                  </div>
                );
              }
              return <div className="font-display text-3xl font-bold text-muted-foreground">VS</div>;
            })()}
            <div className="text-center min-w-[180px]">
              <img src={match.away_team.logo} alt={match.away_team.name}
                   className="w-24 h-24 object-contain mx-auto mb-3" />
              <h2 className="font-display text-2xl font-bold tracking-tight inline-flex items-center gap-1.5">
                {match.away_team.name}
                <FollowTeamButton teamId={match.away_team.id} teamName={match.away_team.name} size="lg" />
              </h2>
            </div>
          </div>

          <div className="text-center text-sm text-muted-foreground mb-5">
            <p>Venue: {match.venue}</p>
          </div>

          <div className="text-center">
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button
                onClick={fetchDetailedAnalysis}
                disabled={loadingAnalysis}
                className="h-11 rounded-full px-6 bg-foreground text-background hover:bg-foreground/90 font-semibold"
              >
                <FileText className="w-4 h-4 mr-2" />
                {loadingAnalysis ? "Loading analysis…" : "View detailed analysis"}
              </Button>
              {/* Iter 124 — Watch 3D Tracker CTA.  Always visible on the
                  match details page (we already have the fixture id from
                  the URL); LiveStreamModal handles the "stream not
                  available" message when aiscore has nothing to show. */}
              <Button
                onClick={() => setShowStream(true)}
                data-testid="md-watch-3d-btn"
                className="h-11 rounded-full px-6 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold shadow-lg shadow-emerald-500/30"
              >
                <Radio className="w-4 h-4 mr-2" />
                Watch 3D Tracker
              </Button>
            </div>
            <p className="text-muted-foreground text-xs mt-3">
              Comprehensive breakdown with H2H, form, stats & expert commentary
            </p>
          </div>
        </section>

        {showAnalysisModal && analysisData && (
          <MatchAnalysisModal analysis={analysisData} onClose={() => setShowAnalysisModal(false)} />
        )}

        <LiveStreamModal
          open={showStream}
          onOpenChange={setShowStream}
          fixtureId={matchId}
          homeTeam={match.home_team?.name || match.home_team}
          awayTeam={match.away_team?.name || match.away_team}
        />

        {/* Iter 108 — Derby ribbon + hit-tracker summary */}
        {(predictions.derby || predictions.tracker_summary) && (
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="md-meta-ribbon">
            {predictions.derby && (
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500/15 to-rose-500/15 border border-amber-500/40 text-amber-500 text-xs font-bold uppercase tracking-[0.12em]"
                data-testid="md-derby-badge"
              >
                <Flame className="w-3.5 h-3.5" />
                {predictions.derby.name}
              </div>
            )}
            {predictions.tracker_summary && predictions.tracker_summary.total > 0 && (
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-card text-xs font-semibold"
                data-testid="md-tracker-summary"
                title="Per-market hit/miss for our pre-match picks"
              >
                {predictions.tracker_status === "finished" ? (
                  <>
                    <span className="text-emerald-500">{predictions.tracker_summary.hit} hit</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-rose-500">{predictions.tracker_summary.miss} miss</span>
                    {predictions.tracker_summary.void > 0 && (
                      <>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground">{predictions.tracker_summary.void} void</span>
                      </>
                    )}
                  </>
                ) : predictions.tracker_status === "live" ? (
                  <>
                    <span className="relative flex w-1.5 h-1.5">
                      <span className="absolute inline-flex w-full h-full rounded-full bg-amber-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-amber-400" />
                    </span>
                    <span>Live tracking · {predictions.tracker_summary.live} markets</span>
                  </>
                ) : (
                  <span className="text-muted-foreground">{predictions.tracker_summary.pending} markets pending kickoff</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Iter 78 — Full Hit Tracker card.  Shows a per-market summary of
            how our pre-match picks performed once the fixture is finished
            (or a live-tracking version while the game is in progress). */}
        {predictions.tracker_summary && predictions.tracker_summary.total > 0 && predictions.tracker_status !== "pending" && (() => {
          const s = predictions.tracker_summary;
          const isFinished = predictions.tracker_status === "finished";
          const settled = (s.hit || 0) + (s.miss || 0);
          const hitRate = settled > 0 ? Math.round((s.hit / settled) * 1000) / 10 : null;
          const rateColour =
            hitRate == null ? "text-muted-foreground" :
            hitRate >= 70 ? "text-emerald-400" :
            hitRate >= 50 ? "text-amber-400" :
            "text-rose-400";
          return (
            <section
              className="nd-card p-6 mb-8"
              data-testid="md-hit-tracker-card"
            >
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                  <h3 className="font-display text-lg font-bold flex items-center gap-2">
                    {isFinished ? "Hit Tracker" : "Hit Tracker · Live"}
                    {!isFinished && (
                      <span className="relative flex w-2 h-2">
                        <span className="absolute inline-flex w-full h-full rounded-full bg-amber-400 opacity-75 animate-ping" />
                        <span className="relative inline-flex w-2 h-2 rounded-full bg-amber-400" />
                      </span>
                    )}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {isFinished
                      ? `How our ${s.total} pre-match markets performed`
                      : `Watching ${s.total} pre-match markets as the game unfolds`}
                  </p>
                </div>
                {isFinished && hitRate != null && (
                  <div className="text-right" data-testid="md-hit-tracker-rate">
                    <div className={`font-display text-3xl font-black tabular-nums ${rateColour}`}>
                      {hitRate}%
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mt-0.5">
                      Hit rate ({s.hit}/{settled})
                    </div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-center" data-testid="hit-tracker-tile-hit">
                  <div className="font-display text-2xl font-black text-emerald-400 tabular-nums">{s.hit || 0}</div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mt-1">Hits</div>
                </div>
                <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-center" data-testid="hit-tracker-tile-miss">
                  <div className="font-display text-2xl font-black text-rose-400 tabular-nums">{s.miss || 0}</div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mt-1">Misses</div>
                </div>
                <div className="rounded-lg border border-border bg-card/60 p-3 text-center" data-testid="hit-tracker-tile-void">
                  <div className="font-display text-2xl font-black text-muted-foreground tabular-nums">{s.void || 0}</div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mt-1">Void</div>
                </div>
                <div className={`rounded-lg border p-3 text-center ${
                  isFinished
                    ? "border-border bg-card/60"
                    : "border-amber-500/30 bg-amber-500/5"
                }`} data-testid="hit-tracker-tile-live">
                  <div className={`font-display text-2xl font-black tabular-nums ${
                    isFinished ? "text-muted-foreground" : "text-amber-400"
                  }`}>{isFinished ? (s.pending || 0) : (s.live || 0)}</div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mt-1">
                    {isFinished ? "Pending" : "Live"}
                  </div>
                </div>
              </div>
            </section>
          );
        })()}

        {/* Iter 81 — Live In-Play Panel (momentum, pressure, next-goal, more-goals) */}
        {livePayload?.is_live && livePayload.in_play && (
          <LiveInPlayPanel
            inPlay={livePayload.in_play}
            homeName={match.home_team?.name}
            awayName={match.away_team?.name}
          />
        )}

        {/* Iter 72 — Smart-bettor signals (xG trajectory + lineup alerts) */}
        {(() => {
          const xgt = predictions.xg_trajectory;
          const alerts = predictions.lineup_alerts || [];
          const showSignals =
            (xgt && (xgt.home === "DUE" || xgt.home === "LUCKY" || xgt.away === "DUE" || xgt.away === "LUCKY")) ||
            alerts.length > 0;
          if (!showSignals) return null;
          return (
            <div
              className="nd-card p-4 mb-6 flex flex-wrap items-center gap-2 border-amber-500/30 bg-amber-500/5"
              data-testid="match-signals-bar"
            >
              <span className="text-[11px] uppercase tracking-[0.12em] font-bold text-muted-foreground/80 mr-1">
                Smart-bettor signals
              </span>
              {xgt?.home === "DUE" && (
                <span className="px-2 py-1 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-500 text-xs font-bold" data-testid="xg-pill-home-due">
                  🎯 {match.home_team?.name} DUE · xG diff {xgt.home_xg_diff}
                </span>
              )}
              {xgt?.home === "LUCKY" && (
                <span className="px-2 py-1 rounded border border-rose-500/40 bg-rose-500/10 text-rose-500 text-xs font-bold" data-testid="xg-pill-home-lucky">
                  🎲 {match.home_team?.name} LUCKY · xG diff {xgt.home_xg_diff}
                </span>
              )}
              {xgt?.away === "DUE" && (
                <span className="px-2 py-1 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-500 text-xs font-bold" data-testid="xg-pill-away-due">
                  🎯 {match.away_team?.name} DUE · xG diff {xgt.away_xg_diff}
                </span>
              )}
              {xgt?.away === "LUCKY" && (
                <span className="px-2 py-1 rounded border border-rose-500/40 bg-rose-500/10 text-rose-500 text-xs font-bold" data-testid="xg-pill-away-lucky">
                  🎲 {match.away_team?.name} LUCKY · xG diff {xgt.away_xg_diff}
                </span>
              )}
              {alerts.map((a, i) => (
                <span
                  key={`alert-${i}`}
                  className="px-2 py-1 rounded border border-amber-500/40 bg-amber-500/10 text-amber-500 text-xs font-bold"
                  data-testid={`lineup-alert-${a.team_side}-${i}`}
                >
                  🚨 {a.team_name} —{" "}
                  {a.missing_players.map((p) => `${p.name} (${p.role.replace("_", " ")}) out`).join(", ")}
                </span>
              ))}
            </div>
          );
        })()}

        {/* Predictions grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {predictions.markets.map((pred, index) => (
            <article
              key={index}
              className="nd-card nd-card-hover p-5 nd-fade-up"
              style={{ animationDelay: `${index * 0.04}s` }}
              data-testid={`prediction-card-${index}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="eyebrow truncate">{pred.market_type}</span>
                  {/* Iter 85d — Engine badge (Q = quantum primary, C = classic). */}
                  {pred._engine === "quantum" && (
                    <span
                      className="inline-flex items-center px-1 py-px rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 text-[9px] font-black tracking-wider"
                      title="Powered by the Quantum ensemble engine (primary)"
                      data-testid={`md-market-engine-quantum-${index}`}
                    >
                      Q
                    </span>
                  )}
                  {pred._engine === "classic" && (
                    <span
                      className="inline-flex items-center px-1 py-px rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[9px] font-black tracking-wider"
                      title="Classic engine (Quantum doesn't publish this market)"
                      data-testid={`md-market-engine-classic-${index}`}
                    >
                      C
                    </span>
                  )}
                  <button
                    onClick={() => setWhyPanelMarket(pred.market_type)}
                    aria-label={`Why ${pred.confidence}% confidence`}
                    data-testid={`md-why-btn-${index}`}
                    className="p-0.5 rounded-full text-muted-foreground hover:text-amber-500 hover:bg-amber-500/10 transition-colors shrink-0"
                  >
                    <HelpCircle className="w-3.5 h-3.5" />
                  </button>
                  <TrustStars
                    score={pred.trust_score}
                    components={pred.trust_components}
                    size="sm"
                    testId={`md-trust-score-${index}`}
                  />
                  {pred.tracker && <HitTrackerBadge tracker={pred.tracker} />}
                </div>
                <span className={`text-[10px] font-bold tracking-[0.06em] uppercase px-2 py-0.5 rounded-md border ${riskTone(pred.risk_level)}`}>
                  {pred.risk_level} risk
                </span>
              </div>
              <div className="flex items-baseline justify-between mb-3 gap-3">
                <span className="font-display text-xl sm:text-2xl font-bold truncate">{pred.prediction}</span>
                <span className={`font-display text-3xl font-bold font-tabular ${confTone(pred.confidence)}`}>
                  {pred.confidence}%
                </span>
              </div>
              <div className="confidence-track mb-3">
                <div className="confidence-fill" style={{ width: `${pred.confidence}%` }} />
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{pred.rationale}</p>
              {/* Iter 85d — Inline compare row showing the OTHER engine's
                  pick, so users can see Quantum vs Classic drift at a
                  glance (mirrors SchedulePage/HomePage). */}
              {pred._engine === "quantum" && pred.classic_alt && (
                <div
                  className="mt-3 pt-3 border-t border-amber-500/20 flex items-center justify-between gap-2 text-[11px]"
                  data-testid={`md-market-classic-alt-${index}`}
                >
                  <span className="inline-flex items-center gap-1 text-amber-300 font-black tracking-widest text-[9px]">
                    CLASSIC
                  </span>
                  <span className="text-foreground/80 truncate flex-1">{pred.classic_alt.prediction}</span>
                  <span className="font-tabular text-foreground/80">
                    {pred.classic_alt.confidence != null ? `${Math.round(pred.classic_alt.confidence)}%` : "—"}
                  </span>
                </div>
              )}
              {pred._engine === "classic" && pred.quantum_alt && (
                <div
                  className="mt-3 pt-3 border-t border-cyan-500/20 flex items-center justify-between gap-2 text-[11px]"
                  data-testid={`md-market-quantum-alt-${index}`}
                >
                  <span className="inline-flex items-center gap-1 text-cyan-300 font-black tracking-widest text-[9px]">
                    QUANTUM
                  </span>
                  <span className="text-foreground/80 truncate flex-1">{pred.quantum_alt.prediction}</span>
                  <span className="font-tabular text-foreground/80">
                    {pred.quantum_alt.confidence != null ? `${Math.round(pred.quantum_alt.confidence)}%` : "—"}
                  </span>
                </div>
              )}
              {pred.derby_note && (
                <div className="mt-3 px-3 py-2 rounded-lg border border-amber-500/40 bg-amber-500/5 text-amber-500 text-[11px] leading-relaxed flex items-start gap-1.5" data-testid={`md-derby-note-${index}`}>
                  <Flame className="w-3 h-3 shrink-0 mt-0.5" />
                  <span>{pred.derby_note}</span>
                </div>
              )}
            </article>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {predictions.form_analysis && (
            <section className="nd-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4" />
                <h2 className="font-display text-lg font-semibold">Team form analysis</h2>
              </div>
              {["home", "away"].map((side, i) => {
                const f = predictions.form_analysis[side];
                const teamName = match[`${side}_team`].name;
                return (
                  <div key={side} className={i > 0 ? "border-t border-border pt-4 mt-4" : ""}>
                    <p className="eyebrow mb-2">{teamName}</p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-muted-foreground">Form: </span><span className="font-semibold">{f?.form_string || "N/A"}</span></div>
                      <div><span className="text-muted-foreground">Points: </span><span className="font-semibold font-tabular">{f?.points || 0}/15</span></div>
                      <div><span className="text-muted-foreground">Scored: </span><span className="font-semibold text-success font-tabular">{f?.goals_scored || 0}</span></div>
                      <div><span className="text-muted-foreground">Conceded: </span><span className="font-semibold text-destructive font-tabular">{f?.goals_conceded || 0}</span></div>
                    </div>
                  </div>
                );
              })}
              <div className="border-t border-border pt-4 mt-4 flex items-center justify-between">
                <span className="eyebrow">Form differential</span>
                <span
                  className="font-display text-lg font-bold font-tabular"
                  style={{
                    color:
                      (predictions.form_analysis.differential || 0) > 0 ? "hsl(var(--success))"
                      : (predictions.form_analysis.differential || 0) < 0 ? "hsl(var(--destructive))"
                      : "hsl(var(--warning))",
                  }}
                >
                  {(predictions.form_analysis.differential || 0) > 0 ? "+" : ""}
                  {predictions.form_analysis.differential || 0}
                </span>
              </div>
            </section>
          )}

          {predictions.h2h_analysis && (
            <section className="nd-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <BarChart2 className="w-4 h-4" />
                <h2 className="font-display text-lg font-semibold">Head-to-head</h2>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="font-display text-2xl font-bold font-tabular text-success">{predictions.h2h_analysis.home_wins || 0}</p>
                  <p className="eyebrow mt-1">Home wins</p>
                </div>
                <div>
                  <p className="font-display text-2xl font-bold font-tabular text-muted-foreground">{predictions.h2h_analysis.draws || 0}</p>
                  <p className="eyebrow mt-1">Draws</p>
                </div>
                <div>
                  <p className="font-display text-2xl font-bold font-tabular text-destructive">{predictions.h2h_analysis.away_wins || 0}</p>
                  <p className="eyebrow mt-1">Away wins</p>
                </div>
              </div>
              <div className="space-y-3 border-t border-border pt-4 mt-4 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Matches played</span>
                  <span className="font-semibold font-tabular">{predictions.h2h_analysis.matches_played || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Avg goals / match</span>
                  <span className="font-semibold font-tabular">{predictions.h2h_analysis.avg_goals?.toFixed(1) || "N/A"}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">BTTS rate</span>
                  <span className="font-semibold font-tabular">
                    {predictions.h2h_analysis.btts_rate ? (predictions.h2h_analysis.btts_rate * 100).toFixed(0) : "0"}%
                  </span>
                </div>
              </div>
            </section>
          )}
        </div>

        {/* Iter 145bd — Side-by-side Classic vs Quantum comparison */}
        {data?.match?.fixture_id && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-8">
            <EngineComparisonPanel fixtureId={parseInt(data.match.fixture_id, 10)} />
          </div>
        )}
      </main>
      {whyPanelMarket && (
        <WhyPanel
          fixtureId={parseInt(matchId, 10)}
          marketType={whyPanelMarket}
          onClose={() => setWhyPanelMarket(null)}
        />
      )}
    </div>
  );
};

export default MatchDetailsPage;
